import React from 'react';

import {CopyButton} from 'src/components/copy-button';
import {Skeleton} from 'src/components/skeleton';

import {AvatarWrapped} from '@pb/kit-component-avatar';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import Typography from '@pb/kit-component-typography';

import {DeleteUser} from './components/delete-user';
import {RestoreUser} from './components/restore-user';
import {UserPageSkeleton} from './user-page-skeleton';

import {
    userPageHeader,
    userPageHeaderId,
} from './user-page-header.module.styl';

import type {UserAuthInfoResponse} from 'src/openapi/admin/models/UserAuthInfoResponse';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';
import type {ServerResponse} from 'src/typedef';


type Props = {
    shortInfo: UserShortInfo | null,
    authInfo: ServerResponse<UserAuthInfoResponse> | null,
    onChangeUser: () => void,
};

export const UserPageHeader = ({shortInfo, onChangeUser, authInfo}: Props) => (
    <Skeleton view={<UserPageSkeleton/>} shown={!shortInfo}>
        {shortInfo ? (
            <Group className={userPageHeader}>
                <Item grow={1}>
                    <Group justify="space-between">
                        <Group alignItems="center" gap="l">
                            <Group>
                                <AvatarWrapped
                                    src={shortInfo.avatar}
                                    alt={(shortInfo.userId).toString()}
                                    placeholder={Icons.USER}
                                    name={`${shortInfo.firstName} ${shortInfo.lastName}`}
                                />
                                <div className="enable-selection">
                                    <Typography variant="heading-level-1">
                                        {shortInfo.firstName} {shortInfo.lastName} {shortInfo.isDeleted
                                            ? '(Deleted)'
                                            : null}
                                    </Typography>
                                </div>
                            </Group>
                            <Group gap="s" alignItems="center">
                                <Group className={`${userPageHeaderId} enable-selection`} gap="s">
                                    <Item>ID</Item>
                                    <Item>{shortInfo.userId}</Item>
                                </Group>
                                <CopyButton
                                    copiedText={shortInfo.userId.toString() || ''}
                                    entityName="User ID"
                                    iconSize={Sizes.Size_16}
                                />
                            </Group>
                            <Group gap="s" alignItems="center">
                                <Group className={`${userPageHeaderId} enable-selection`} gap="s">
                                    <Item>M* GUID</Item>
                                    <Item style={{maxWidth: 150}}>
                                        {authInfo?.data?.guid ? (
                                            <EllipsisTextHint title={authInfo.data.guid}>
                                                {authInfo.data.guid}
                                            </EllipsisTextHint>
                                        ) : 'Missing'}
                                    </Item>
                                </Group>
                                {authInfo?.data?.guid ? (
                                    <CopyButton
                                        copiedText={authInfo.data.guid || ''}
                                        entityName="M* GUID ID"
                                        iconSize={Sizes.Size_16}
                                    />
                                ) : null}
                            </Group>
                        </Group>
                        {shortInfo?.isDeleted ? (
                            <RestoreUser
                                userId={shortInfo.userId}
                                accountId={shortInfo.accountId}
                                onDone={onChangeUser}
                            />
                        ) : (
                            <DeleteUser
                                userId={shortInfo.userId}
                                accountId={shortInfo.accountId}
                                onDone={onChangeUser}
                            />
                        )}
                    </Group>
                </Item>
            </Group>
        ) : null}
    </Skeleton>
);
